/**
 * Günsonu kasa — A4 dikey PDF (2 sayfa).
 * Sayfa 1: Kasa | Kısa Rapor / Firma | Mağaza / Şube Karşılaştırma
 * Sayfa 2: Bugünün İşlemleri
 */
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { buildGunsonuDashboardPayload } from './gunsonuPdfInsight';
import { drawFinanceSummaryPage } from './finance/financeSummaryPdf';
import { ensureTurkishFonts } from './pdfTurkishFont';
import { formatQtyTr } from './gunsonuWhatsappReport';

// Fontlar ortak modulde: financeSummaryPdf de ayni yukleyiciyi kullaniyor,
// iki dosya birbirini import edince dongu olusuyordu.
export { ensureTurkishFonts } from './pdfTurkishFont';

function fileSafe(name) {
  return String(name ?? '')
    .replace(/ğ/g, 'g')
    .replace(/Ğ/g, 'G')
    .replace(/ü/g, 'u')
    .replace(/Ü/g, 'U')
    .replace(/ş/g, 's')
    .replace(/Ş/g, 'S')
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'I')
    .replace(/ö/g, 'o')
    .replace(/Ö/g, 'O')
    .replace(/ç/g, 'c')
    .replace(/Ç/g, 'C')
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 80);
}

function drawPanelTitle(doc, title, x, y, w) {
  doc.setFillColor(23, 56, 91);
  doc.roundedRect(x, y, w, 7, 1, 1, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('DejaVuSans', 'bold');
  doc.setFontSize(9);
  doc.text(String(title), x + 2.2, y + 4.8);
}

function drawPageHeader(doc, payload, pageW, margin) {
  doc.setFillColor(15, 36, 66);
  doc.rect(0, 0, pageW, 16, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('DejaVuSans', 'bold');
  doc.setFontSize(15);
  doc.text(String(payload.branchLabel || 'UKBA'), margin, 10.5);
  doc.setFont('DejaVuSans', 'normal');
  doc.setFontSize(10);
  doc.text(String(payload.dateLabel || ''), pageW - margin, 10.5, { align: 'right' });
}

function drawPageFooters(doc, pageW, pageH, margin) {
  const count = doc.internal.getNumberOfPages();
  for (let i = 1; i <= count; i += 1) {
    doc.setPage(i);
    doc.setFont('DejaVuSans', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text('UKBA Kasa · günsonu PDF', pageW / 2, pageH - 3.5, { align: 'center' });
    doc.text(`${i} / ${count}`, pageW - margin, pageH - 3.5, { align: 'right' });
  }
}

/** Kısa rapor: başlık + etiket/değer satırları. */
function drawInsightBlocks(doc, blocks, x, y, width, maxY) {
  const labelW = Math.min(34, Math.max(26, width * 0.36));
  const valueW = Math.max(18, width - labelW - 1);
  let cy = y;

  for (const block of blocks || []) {
    const rows = Array.isArray(block?.rows) ? block.rows : [];
    if (cy + 9 > maxY) break;

    doc.setFont('DejaVuSans', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(23, 56, 91);
    doc.text(String(block.title || ''), x, cy);
    cy += 1.5;
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.25);
    doc.line(x, cy, x + width, cy);
    cy += 3.5;

    let stopped = false;
    for (const [label, value] of rows) {
      doc.setFont('DejaVuSans', 'normal');
      doc.setFontSize(8);
      const valueLines = doc.splitTextToSize(String(value || '—'), valueW);
      const rowH = Math.max(4.6, valueLines.length * 3.8);
      if (cy + rowH - 1 > maxY) {
        stopped = true;
        break;
      }

      doc.setTextColor(100, 116, 139);
      doc.text(String(label || ''), x, cy);
      doc.setFont('DejaVuSans', 'bold');
      doc.setFontSize(8.1);
      doc.setTextColor(30, 41, 59);
      doc.text(valueLines, x + labelW, cy);
      cy += rowH;
    }
    if (stopped) break;
    cy += 2.2;
  }
}

const BRANCH_BAR_COLORS = {
  sefakoy_merkez: [23, 56, 91],
  sefakoy_sube: [37, 99, 235],
  kavakli_sube: [13, 148, 136],
};

function compactCiro(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('tr-TR', { maximumFractionDigits: 0 });
}

function compactGram(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function drawBranchComparePanel(doc, branches, x, y, width, height) {
  const list = Array.isArray(branches) && branches.length ? branches : [];
  const groups = [
    [
      { key: 'islemCount', title: 'İşlem', format: (v) => String(Number(v) || 0) },
      { key: 'satisCount', title: 'Satış', format: (v) => String(Number(v) || 0) },
      { key: 'ciro', title: 'Ciro (TL)', format: compactCiro },
      { key: 'nakit', title: 'Nakit (TL)', format: compactCiro },
    ],
    [
      { key: 'takiGram', title: 'Takı satışı (g)', format: compactGram },
      { key: 'gramAltinGram', title: 'Gram altın satışı (g)', format: compactGram },
      { key: 'sarrafiyeAdet', title: 'Sarrafiye satışı (adet)', format: (v) => String(Number(v) || 0) },
    ],
  ];

  const groupGap = 3;
  const groupH = (height - groupGap) / groups.length;

  groups.forEach((metrics, gi) => {
    const gy = y + gi * (groupH + groupGap);
    const innerX = x + 2;
    const innerW = width - 4;
    const colGap = 2.5;
    const colW = (innerW - colGap * (metrics.length - 1)) / metrics.length;
    const startY = gy + 3.2;
    const rowH = Math.max(10.2, (groupH - 9) / Math.max(3, list.length));

    metrics.forEach((metric, mi) => {
      const cx = innerX + mi * (colW + colGap);
      doc.setFont('DejaVuSans', 'bold');
      doc.setFontSize(7.1);
      doc.setTextColor(23, 56, 91);
      doc.text(metric.title, cx, startY);

      const maxVal = Math.max(1, ...list.map((b) => Number(b[metric.key]) || 0));
      list.forEach((branch, bi) => {
        const by = startY + 3.6 + bi * rowH;
        const color = BRANCH_BAR_COLORS[branch.key] || [71, 85, 105];
        if (branch.isCurrent) {
          doc.setFillColor(241, 245, 249);
          doc.roundedRect(cx - 0.5, by - 1.1, colW + 1, rowH - 1.3, 0.7, 0.7, 'F');
        }

        doc.setFont('DejaVuSans', branch.isCurrent ? 'bold' : 'normal');
        doc.setFontSize(6.3);
        doc.setTextColor(71, 85, 105);
        const name = `${branch.shortLabel || branch.label || ''}${branch.isCurrent ? '  ●' : ''}`;
        doc.text(name, cx, by + 2);

        const val = Number(branch[metric.key]) || 0;
        doc.setFont('DejaVuSans', 'bold');
        doc.setFontSize(6.8);
        doc.setTextColor(30, 41, 59);
        doc.text(metric.format(val), cx + colW - 0.3, by + 2, { align: 'right' });

        const barMax = colW - 0.6;
        const barW = val > 0 ? Math.max(2.2, (val / maxVal) * barMax) : 4.5;
        if (val > 0) doc.setFillColor(color[0], color[1], color[2]);
        else doc.setFillColor(226, 232, 240);
        doc.roundedRect(cx, by + 3.2, barW, 3.3, 0.6, 0.6, 'F');
      });
    });
  });
}

/** Tahmini satır yüksekliği (mm) — taşmayı önlemek için. */
function estimateRowHeightMm(fontSize, cellPadding = 1.0) {
  return fontSize * 0.3528 + cellPadding * 2 + 0.35;
}

function maxRowsForHeight(availableH, fontSize, hasHead = true, cellPadding = 1.0) {
  const rh = estimateRowHeightMm(fontSize, cellPadding);
  const usable = Math.max(0, availableH - (hasHead ? rh : 0) - 0.5);
  return Math.max(1, Math.floor(usable / rh));
}

function scaleColumnStyles(columnStyles, fromWidth, toWidth) {
  if (!columnStyles || !fromWidth || fromWidth <= 0) return columnStyles;
  const ratio = toWidth / fromWidth;
  const next = {};
  Object.entries(columnStyles).forEach(([key, style]) => {
    next[key] = { ...style };
    if (typeof style.cellWidth === 'number') {
      next[key].cellWidth = Math.max(8, style.cellWidth * ratio);
    }
  });
  return next;
}

function panelTable(doc, { startY, left, width, head, body, columnStyles, fontSize = 7.4, cellPadding = 1.0 }) {
  autoTable(doc, {
    startY,
    margin: { left, right: doc.internal.pageSize.getWidth() - left - width },
    tableWidth: width,
    theme: 'plain',
    head: head ? [head] : undefined,
    body: body.length ? body : [['—']],
    styles: {
      font: 'DejaVuSans',
      fontSize,
      cellPadding,
      textColor: [30, 41, 59],
      overflow: 'ellipsize',
      valign: 'middle',
    },
    headStyles: {
      fillColor: [236, 242, 248],
      textColor: [71, 85, 105],
      fontStyle: 'bold',
      fontSize,
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles,
    showHead: head ? 'firstPage' : 'never',
    pageBreak: 'avoid',
    rowPageBreak: 'avoid',
  });
  return doc.lastAutoTable?.finalY || startY;
}

/**
 * Panel yüksekliğine sığdırır. Tek sütun yetmezse iki yan yana tabloya böler.
 * totalRow verilirse her zaman sağ sütunun sonuna eklenir (GENEL TOPLAM vb.).
 */
function fitPanelTable(
  doc,
  {
    startY,
    left,
    width,
    maxY,
    head,
    body,
    columnStyles,
    fontSize = 7.4,
    totalRow = null,
  }
) {
  const availableH = Math.max(8, maxY - startY);
  const dataBody = (body && body.length ? body : [['—']]).map((r) => [...r]);
  const hasHead = Boolean(head);

  const maxSingle = maxRowsForHeight(availableH, fontSize, hasHead);
  const fitsSingle =
    dataBody.length + (totalRow ? 1 : 0) <= maxSingle && dataBody.length <= maxSingle;

  if (fitsSingle) {
    const singleBody = totalRow ? [...dataBody, totalRow] : dataBody;
    return panelTable(doc, {
      startY,
      left,
      width,
      head,
      body: singleBody,
      columnStyles,
      fontSize,
    });
  }

  // İki sütun — biraz daha sıkı tipografi
  const dualFs = Math.max(6.0, fontSize - 0.6);
  const dualPad = 0.75;
  const gapInner = 2;
  const halfW = (width - gapInner) / 2;
  const maxPerCol = maxRowsForHeight(availableH, dualFs, hasHead, dualPad);
  // Sağ sütunda total varsa bir satır ayır
  const rightBudget = Math.max(1, maxPerCol - (totalRow ? 1 : 0));
  const leftBudget = maxPerCol;
  const capacity = leftBudget + rightBudget;
  const clipped = dataBody.slice(0, capacity);
  const mid = Math.min(leftBudget, Math.ceil(clipped.length / 2));
  let leftBody = clipped.slice(0, mid);
  let rightBody = clipped.slice(mid);
  if (totalRow) rightBody = [...rightBody, totalRow];
  if (!leftBody.length) leftBody = [['—']];
  if (!rightBody.length) rightBody = [['—']];

  const halfStyles = scaleColumnStyles(columnStyles, width, halfW);
  panelTable(doc, {
    startY,
    left,
    width: halfW,
    head,
    body: leftBody,
    columnStyles: halfStyles,
    fontSize: dualFs,
    cellPadding: dualPad,
  });
  return panelTable(doc, {
    startY,
    left: left + halfW + gapInner,
    width: halfW,
    head,
    body: rightBody,
    columnStyles: halfStyles,
    fontSize: dualFs,
    cellPadding: dualPad,
  });
}

/**
 * @returns {Promise<{ blob: Blob, base64: string, filename: string, dataUri: string, insight: string }>}
 */
export async function buildGunsonuPdf({
  subeKey,
  dateLabel,
  kasaData,
  allIslemler,
  todayIslemler,
  lastWeekIslemler,
  magazaRows,
  firmaRows,
  firmaGenelToplam,
  islemler,
  branchCompare,
  // Finans ozeti AYRI dosya degil, bu PDF'in son sayfasi olarak eklenir.
  finance = null,
}) {
  const today = todayIslemler || islemler || [];
  const payload = buildGunsonuDashboardPayload({
    subeKey,
    dateLabel,
    kasaData,
    allIslemler: allIslemler || today,
    todayIslemler: today,
    lastWeekIslemler: lastWeekIslemler || [],
    magazaRows: magazaRows || null,
    firmaRows: firmaRows || null,
    firmaGenelToplam,
    branchCompare,
  });

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  await ensureTurkishFonts(doc);

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 8;
  const gap = 3;
  const colW = (pageW - margin * 2 - gap) / 2;
  const leftX = margin;
  const rightX = margin + colW + gap;

  drawPageHeader(doc, payload, pageW, margin);

  const topY = 19;
  const footerReserve = 8;
  const usableH = pageH - topY - footerReserve - gap * 2;
  const cmpH = 92;
  const remaining = Math.max(80, usableH - cmpH);
  const topH = Math.round(remaining * 0.52);
  const midH = remaining - topH;
  const midY = topY + topH + gap;
  const cmpY = midY + midH + gap;

  doc.setDrawColor(210, 220, 232);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(leftX, topY, colW, topH, 1.5, 1.5, 'FD');
  doc.roundedRect(rightX, topY, colW, topH, 1.5, 1.5, 'FD');
  doc.roundedRect(leftX, midY, colW, midH, 1.5, 1.5, 'FD');
  doc.roundedRect(rightX, midY, colW, midH, 1.5, 1.5, 'FD');
  doc.roundedRect(margin, cmpY, pageW - margin * 2, cmpH, 1.5, 1.5, 'FD');

  const topMaxY = topY + topH - 2;
  const midMaxY = midY + midH - 2;
  const panelInnerW = colW - 3;
  const fullInnerW = pageW - margin * 2 - 3;

  // Sol üst — Günsonu Kasa
  drawPanelTitle(doc, 'Günsonu Kasa (Mevcut Kasa)', leftX + 1.5, topY + 1.5, panelInnerW);
  fitPanelTable(doc, {
    startY: topY + 10,
    left: leftX + 1.5,
    width: panelInnerW,
    maxY: topMaxY,
    head: ['Kalem', 'Miktar'],
    body: payload.kasaRows.length
      ? payload.kasaRows.map((r) => [
          String(r.label),
          `${r.value}${r.unit ? ` ${r.unit}` : ''}`,
        ])
      : [['Dolu kalem yok', '']],
    columnStyles: {
      0: { cellWidth: panelInnerW - 27 },
      1: { cellWidth: 24, halign: 'right' },
    },
    fontSize: 7.5,
  });

  // Sağ üst — Kısa Rapor (satır satır)
  drawPanelTitle(doc, 'Kısa Rapor', rightX + 1.5, topY + 1.5, panelInnerW);
  drawInsightBlocks(
    doc,
    payload.insightBlocks || [],
    rightX + 3.2,
    topY + 12.5,
    panelInnerW - 4.2,
    topMaxY
  );

  // Sol alt — Firma Bakiyeler
  drawPanelTitle(doc, 'Firma Bakiyeler', leftX + 1.5, midY + 1.5, panelInnerW);
  const firmaDataBody = payload.firmaRows.length
    ? payload.firmaRows.map((r) => [
        String(r.firmaIsmi),
        formatQtyTr(r.toplamHas, 2),
      ])
    : [['Firma bakiyesi yok', '']];
  const firmaTotal =
    payload.firmaRows.length > 0
      ? ['GENEL TOPLAM', formatQtyTr(payload.firmaGenelToplam, 2)]
      : null;
  fitPanelTable(doc, {
    startY: midY + 10,
    left: leftX + 1.5,
    width: panelInnerW,
    maxY: midMaxY,
    head: ['Firma', 'Has (g)'],
    body: firmaDataBody,
    totalRow: firmaTotal,
    columnStyles: {
      0: { cellWidth: panelInnerW - 27 },
      1: { cellWidth: 24, halign: 'right' },
    },
    fontSize: 7.2,
  });

  // Sağ alt — Mağaza Durumu
  drawPanelTitle(doc, 'Mağaza Durumu', rightX + 1.5, midY + 1.5, panelInnerW);
  fitPanelTable(doc, {
    startY: midY + 10,
    left: rightX + 1.5,
    width: panelInnerW,
    maxY: midMaxY,
    head: ['Başlık', 'Durum'],
    body: (payload.magazaRows || []).map((r) => [String(r.label), String(r.detail)]),
    columnStyles: {
      0: { cellWidth: 41 },
      1: { cellWidth: panelInnerW - 44 },
    },
    fontSize: 7.4,
  });

  // Alt tam genişlik — Şube Karşılaştırma
  drawPanelTitle(doc, 'Şube Karşılaştırma · bugün', margin + 1.5, cmpY + 1.5, fullInnerW);
  drawBranchComparePanel(
    doc,
    payload.branchCompare || [],
    margin + 1.5,
    cmpY + 9,
    fullInnerW,
    cmpH - 12
  );

  // Sayfa 2 — Bugünün İşlemleri
  doc.addPage();
  const islemCount = payload.islemRows.length;
  const islemTitle =
    islemCount > 0
      ? `Bugünün İşlemleri · ${islemCount} kayıt`
      : 'Bugünün İşlemleri';
  const islemTableTop = 28;
  const islemBody = payload.islemRows.length
    ? payload.islemRows.map((r) => [
        String(r.saat || '—'),
        String(r.tur),
        String(r.kim),
        String(r.urun),
        String(r.qty),
        String(r.tutar),
      ])
    : [['', 'İşlem yok', '', '', '', '']];

  autoTable(doc, {
    startY: islemTableTop,
    margin: {
      top: islemTableTop,
      left: margin + 1.5,
      right: margin + 1.5,
      bottom: 10,
    },
    tableWidth: fullInnerW - 1,
    theme: 'plain',
    head: [['Saat', 'Tür', 'Kim', 'Ürün', 'Miktar', 'Tutar']],
    body: islemBody,
    styles: {
      font: 'DejaVuSans',
      fontSize: 8,
      cellPadding: 1.35,
      textColor: [30, 41, 59],
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: [236, 242, 248],
      textColor: [71, 85, 105],
      fontStyle: 'bold',
      fontSize: 8,
      overflow: 'ellipsize',
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      0: { cellWidth: 16, overflow: 'ellipsize' },
      1: { cellWidth: 20, overflow: 'ellipsize' },
      2: { cellWidth: 50 },
      3: { cellWidth: 50 },
      4: { cellWidth: 26, overflow: 'ellipsize' },
      5: { cellWidth: 27, halign: 'right', overflow: 'ellipsize' },
    },
    showHead: 'everyPage',
    pageBreak: 'auto',
    rowPageBreak: 'avoid',
    didDrawPage: () => {
      drawPageHeader(doc, payload, pageW, margin);
      drawPanelTitle(doc, islemTitle, margin + 1.5, 19, fullInnerW);
    },
  });

  // --- Son sayfa: finans ozeti (ay basindan bugune). Uretilemezse rapor yine gider.
  let financeError = null;
  if (finance) {
    try {
      doc.addPage();
      await drawFinanceSummaryPage(doc, { ...finance, embedded: true });
    } catch (finErr) {
      financeError = finErr?.message || 'Finans özeti sayfası eklenemedi';
      // Yarim kalan sayfayi at, gun sonu raporu bozulmasin.
      const last = doc.internal.getNumberOfPages();
      if (last > 1) doc.deletePage(last);
    }
  }

  drawPageFooters(doc, pageW, pageH, margin);

  const filename = `UKBA_Gunsonu_${fileSafe(payload.branchLabel)}_${fileSafe(
    String(dateLabel || '').slice(0, 40) || 'rapor'
  )}.pdf`;
  const dataUri = doc.output('datauristring');
  const base64 = dataUri.includes(',') ? dataUri.split(',')[1] : dataUri;
  const blob = doc.output('blob');

  return { blob, base64, filename, dataUri, insight: payload.insight, financeError };
}

export function downloadGunsonuPdf(pdf) {
  if (!pdf?.blob || !pdf?.filename) return;
  const url = URL.createObjectURL(pdf.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = pdf.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
